import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Typography, Button, TextField,
  IconButton, CircularProgress, Stack, Divider, Alert, Tooltip,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import Swal from "sweetalert2";
import apiClient from "@/api/client";

const TYPE_WANTED = "wanted";
const TYPE_UNWANTED = "unwanted";

const newRowKey = (type) =>
  `new-${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const emptyItem = (type) => ({
  _key: newRowKey(type),
  id: null,
  type,
  name: "",
  note: "",
  is_active: true,
  _isNew: true,
  _original: { name: "", note: "", is_active: true },
});

const toDraft = (serverItem) => ({
  _key: `id-${serverItem.id}`,
  id: serverItem.id,
  type: serverItem.type,
  name: serverItem.name ?? "",
  note: serverItem.note ?? "",
  is_active: !!serverItem.is_active,
  _isNew: false,
  _original: {
    name: serverItem.name ?? "",
    note: serverItem.note ?? "",
    is_active: !!serverItem.is_active,
  },
});

const DanaPirikaraItems = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [original, setOriginal] = useState({ wanted: [], unwanted: [] });
  const [draft, setDraft] = useState({ wanted: [], unwanted: [] });
  const [dragState, setDragState] = useState({ type: null, index: null });
  const [dropTarget, setDropTarget] = useState({ type: null, index: null });

  const fetchItems = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await apiClient.get("/pirikara-items/admin");
      const items = res.data?.data ?? [];
      const grouped = { wanted: [], unwanted: [] };
      items.forEach((it) => {
        const bucket = it.type === TYPE_UNWANTED ? TYPE_UNWANTED : TYPE_WANTED;
        grouped[bucket].push(toDraft(it));
      });
      grouped.wanted.sort((a, b) => (a._origSort ?? 0) - (b._origSort ?? 0));
      grouped.unwanted.sort((a, b) => (a._origSort ?? 0) - (b._origSort ?? 0));

      setOriginal({ wanted: [...grouped.wanted], unwanted: [...grouped.unwanted] });
      setDraft({ wanted: [...grouped.wanted], unwanted: [...grouped.unwanted] });
    } catch (err) {
      setLoadError(
        err.response?.data?.message || err.message || "Failed to load items"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // ---- mutations on draft ----
  const addItem = (type) =>
    setDraft((prev) => ({ ...prev, [type]: [...prev[type], emptyItem(type)] }));

  const removeItem = (type, index) =>
    setDraft((prev) => {
      const next = [...prev[type]];
      next.splice(index, 1);
      return { ...prev, [type]: next };
    });

  const updateItem = (type, index, field, value) =>
    setDraft((prev) => {
      const next = [...prev[type]];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, [type]: next };
    });

  const toggleActive = (type, index) =>
    setDraft((prev) => {
      const next = [...prev[type]];
      next[index] = { ...next[index], is_active: !next[index].is_active };
      return { ...prev, [type]: next };
    });

  // ---- drag and drop (handle-only) ----
  const onHandleDragStart = (type, index, e) => {
    setDragState({ type, index });
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires data to be set
    e.dataTransfer.setData("text/plain", `${type}:${index}`);
  };

  const onRowDragOver = (type, index, e) => {
    if (dragState.type !== type) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTarget({ type, index });
  };

  const onRowDrop = (type, index, e) => {
    e.preventDefault();
    const from = dragState.index;
    const okType = dragState.type === type;
    setDragState({ type: null, index: null });
    setDropTarget({ type: null, index: null });
    if (!okType || from === null || from === index) return;

    setDraft((prev) => {
      const next = [...prev[type]];
      const [moved] = next.splice(from, 1);
      next.splice(index, 0, moved);
      return { ...prev, [type]: next };
    });
  };

  const onDragEnd = () => {
    setDragState({ type: null, index: null });
    setDropTarget({ type: null, index: null });
  };

  // ---- save ----
  const handleSave = async () => {
    // client-side validation: no empty names
    for (const type of [TYPE_WANTED, TYPE_UNWANTED]) {
      if (draft[type].some((it) => !it.name.trim())) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Item name cannot be empty.",
          confirmButtonColor: "#c49b36",
        });
        return;
      }
    }

    setSaving(true);
    try {
      for (const type of [TYPE_WANTED, TYPE_UNWANTED]) {
        const origIds = new Set(original[type].map((o) => o.id));
        const draftIds = new Set(draft[type].filter((d) => d.id).map((d) => d.id));

        // 1. delete items removed locally
        const toDelete = [...origIds].filter((id) => !draftIds.has(id));
        for (const id of toDelete) {
          await apiClient.delete(`/pirikara-items/${id}`);
        }

        // 2. create new items, remembering the returned IDs
        const newIdByKey = {};
        for (let i = 0; i < draft[type].length; i++) {
          const it = draft[type][i];
          if (!it._isNew) continue;
          const res = await apiClient.post("/pirikara-items", {
            type,
            name: it.name.trim(),
            note: it.note ? it.note : null,
            sort_order: i,
            is_active: it.is_active,
          });
          newIdByKey[it._key] = res.data?.data?.id ?? null;
        }

        // 3. update existing items that actually changed
        for (const it of draft[type]) {
          if (it._isNew || !it.id) continue;
          const o = it._original || {};
          const changed =
            (o.name ?? "") !== it.name ||
            (o.note ?? "") !== (it.note ?? "") ||
            !!o.is_active !== !!it.is_active;
          if (changed) {
            await apiClient.put(`/pirikara-items/${it.id}`, {
              name: it.name.trim(),
              note: it.note ? it.note : null,
              is_active: it.is_active,
            });
          }
        }

        // 4. reorder to reflect the local sequence
        const reorderItems = draft[type]
          .map((it, i) => {
            const id = it._isNew ? newIdByKey[it._key] : it.id;
            return id ? { id, sort_order: i } : null;
          })
          .filter(Boolean);

        if (reorderItems.length > 0) {
          await apiClient.patch("/pirikara-items/reorder", { items: reorderItems });
        }
      }

      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Pirikara lists updated successfully.",
        confirmButtonColor: "#c49b36",
      });
      await fetchItems();
    } catch (err) {
      const errors = err.response?.data?.errors;
      const msg =
        (errors && Object.values(errors)[0]?.[0]) ||
        err.response?.data?.message ||
        err.message ||
        "Failed to save";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
        confirmButtonColor: "#c49b36",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    Swal.fire({
      title: "Discard changes?",
      text: "All unsaved changes will be lost.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c49b36",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, discard",
    }).then((res) => {
      if (res.isConfirmed) {
        setDraft({
          wanted: original.wanted.map((o) => ({ ...o })),
          unwanted: original.unwanted.map((o) => ({ ...o })),
        });
      }
    });
  };

  // ---- rendering ----
  const renderList = (type, heading) => {
    const items = draft[type];
    return (
      <Card variant="outlined" sx={{ flex: 1, minWidth: 340 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">{heading}</Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => addItem(type)}
              disabled={saving}
            >
              Add item
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {items.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 3, textAlign: "center" }}
            >
              No items yet. Click &ldquo;Add item&rdquo; to create one.
            </Typography>
          )}

          <Stack spacing={1}>
            {items.map((item, index) => {
              const isDragging =
                dragState.type === type && dragState.index === index;
              const isDropTarget =
                dropTarget.type === type && dropTarget.index === index;
              return (
                <Box
                  key={item._key}
                  onDragOver={(e) => onRowDragOver(type, index, e)}
                  onDrop={(e) => onRowDrop(type, index, e)}
                  onDragEnd={onDragEnd}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1,
                    border: "1px solid",
                    borderColor: isDropTarget ? "#1976d2" : "#e0e0e0",
                    borderRadius: 1,
                    backgroundColor: isDragging
                      ? "#e3f2fd"
                      : item.is_active
                      ? "#fff"
                      : "#f7f7f7",
                    opacity: isDragging ? 0.6 : 1,
                    transition:
                      "background-color 0.15s, border-color 0.15s, opacity 0.15s",
                  }}
                >
                  {/* Drag handle — the only draggable element, so text
                      selection inside the inputs keeps working normally. */}
                  <Box
                    draggable={!saving}
                    onDragStart={(e) => onHandleDragStart(type, index, e)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: saving ? "default" : "grab",
                      color: "text.secondary",
                      px: 0.5,
                      "&:active": { cursor: "grabbing" },
                    }}
                    title="Drag to reorder"
                  >
                    <DragIndicatorIcon fontSize="small" />
                  </Box>

                  <TextField
                    size="small"
                    placeholder="Item name"
                    value={item.name}
                    onChange={(e) =>
                      updateItem(type, index, "name", e.target.value)
                    }
                    disabled={saving}
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    size="small"
                    placeholder="Note (optional)"
                    value={item.note}
                    onChange={(e) =>
                      updateItem(type, index, "note", e.target.value)
                    }
                    disabled={saving}
                    sx={{ flex: 1.5 }}
                  />

                  <Tooltip
                    title={
                      item.is_active
                        ? "Visible on public page — click to hide"
                        : "Hidden from public page — click to show"
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={() => toggleActive(type, index)}
                      disabled={saving}
                    >
                      {item.is_active ? (
                        <VisibilityIcon fontSize="small" />
                      ) : (
                        <VisibilityOffIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete this item">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeItem(type, index)}
                      disabled={saving}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h5">
          Pirikara Items — Wanted &amp; Unwanted
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
            disabled={saving || loading}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            startIcon={
              saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
            }
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? "Saving..." : "Save all"}
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Drag rows by the handle to reorder. The eye icon hides an item from the
        public page (the item stays here so you can re-enable it later). Changes
        are only applied when you click <strong>Save all</strong>.
      </Alert>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && loadError && <Alert severity="error">{loadError}</Alert>}

      {!loading && !loadError && (
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          {renderList(TYPE_WANTED, "-Wanted-")}
          {renderList(TYPE_UNWANTED, "-Unwanted-")}
        </Box>
      )}
    </Box>
  );
};

export default DanaPirikaraItems;